import { Injectable } from '@angular/core';
import {
  AuthFlowStateBase,
  AuthMethodRegistrationRequiredState,
  AuthMethodVerificationRequiredState,
  AuthenticationMethod,
  CustomAuthAccountData,
  CustomAuthPublicClientApplication,
  ICustomAuthPublicClientApplication,
  MfaAwaitingState,
  MfaVerificationRequiredState,
  ResetPasswordCodeRequiredState,
  ResetPasswordCompletedState,
  ResetPasswordPasswordRequiredState,
  SignInCodeRequiredState,
  SignInPasswordRequiredState,
  SignUpCodeRequiredState,
  SignUpCompletedState,
  SignUpPasswordRequiredState,
  UserAccountAttributes,
} from '@azure/msal-browser/custom-auth';
import {
  customAuthConfig,
  ENTRA_NATIVE_CLIENT_ID,
  ENTRA_SOCIAL_OAUTH_SCOPES,
  ENTRA_TOKEN_SCOPES,
} from '../config/auth-config';
import {
  logGoogleFlow,
  logGoogleFlowRedirect,
} from '../utils/google-auth-flow-log';
import {
  getStashedEntraRefreshToken,
  getStashedEntraIdToken,
} from '../utils/entra-native-token-capture';

export type SocialProvider = 'google' | 'apple';

export type EntraChallengeStep =
  | 'password'
  | 'code'
  | 'mfa-methods'
  | 'mfa-challenge'
  | 'auth-method-registration'
  | 'auth-method-challenge'
  | 'new-password'
  | 'completed';

export interface EntraFlowResult {
  step: EntraChallengeStep;
  accountData?: CustomAuthAccountData;
  authMethods?: AuthenticationMethod[];
  error?: string;
  /** Sign-in failed because the account does not exist yet. */
  userNotFound?: boolean;
  /** Sign-up failed because the account already exists. */
  userAlreadyExists?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class EntraAuthService {
  private authClientPromise: Promise<ICustomAuthPublicClientApplication>;
  private authClient: ICustomAuthPublicClientApplication | null = null;
  private flowState: AuthFlowStateBase | undefined;
  private static readonly REDIRECT_ERROR_KEY = 'he_entra_redirect_error';
  private static readonly SOCIAL_OAUTH_ABORTED_KEY = 'he_social_oauth_aborted';

  constructor() {
    this.authClientPromise = this.init();
  }

  /** OAuth error in query or hash (e.g. Google Cancel → access_denied). Call before stripping URL. */
  peekOAuthFailureInBrowser(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    const err = (params.get('error') || '').trim();
    if (err) {
      return true;
    }
    const hash = window.location.hash || '';
    return /(^|[?#&])error=/i.test(hash);
  }

  markSocialOAuthAborted(): void {
    try {
      sessionStorage.setItem(EntraAuthService.SOCIAL_OAUTH_ABORTED_KEY, '1');
    } catch {
      // ignore
    }
  }

  consumeSocialOAuthAbortedFlag(): boolean {
    try {
      const v =
        sessionStorage.getItem(EntraAuthService.SOCIAL_OAUTH_ABORTED_KEY) ===
        '1';
      if (v) {
        sessionStorage.removeItem(EntraAuthService.SOCIAL_OAUTH_ABORTED_KEY);
      }
      return v;
    } catch {
      return false;
    }
  }

  clearSocialOAuthAbortedFlag(): void {
    try {
      sessionStorage.removeItem(EntraAuthService.SOCIAL_OAUTH_ABORTED_KEY);
    } catch {
      // ignore
    }
  }

  /**
   * User returned from Google/Apple redirect without an Entra account (e.g. Cancel).
   * Avoids re-attaching ?provider= on /login which would auto-start IdP again.
   */
  noteSocialRedirectAborted(): void {
    try {
      if (!sessionStorage.getItem(EntraAuthService.REDIRECT_ERROR_KEY)) {
        sessionStorage.setItem(
          EntraAuthService.REDIRECT_ERROR_KEY,
          'Sign-in was cancelled.'
        );
      }
      sessionStorage.removeItem('he_social_redirect_pending');
      sessionStorage.setItem(EntraAuthService.SOCIAL_OAUTH_ABORTED_KEY, '1');
    } catch {
      // ignore
    }
    this.clearStuckInteraction();
  }

  /** User-facing message after a failed loginRedirect (shown on /login). */
  consumeRedirectError(): string | null {
    try {
      const message = sessionStorage.getItem(
        EntraAuthService.REDIRECT_ERROR_KEY
      );
      sessionStorage.removeItem(EntraAuthService.REDIRECT_ERROR_KEY);
      return message;
    } catch {
      return null;
    }
  }

  private recordRedirectError(err: unknown): void {
    const raw =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
          ? err
          : 'Sign-in redirect failed.';
    let message = raw;
    if (/AADSTS40015|identity provider returned an error/i.test(raw)) {
      message =
        'Google or Apple sign-in did not complete (Entra federation error). ' +
        'If you registered with email, sign in with email instead — we can link Google after you are signed in.';
    } else if (/AADSTS50011|redirect_uri/i.test(raw)) {
      message =
        'Redirect URI mismatch. Ensure this app URL is registered as a SPA redirect in Entra.';
    } else if (
      /access_denied|user_cancel|cancelled|canceled|consent/i.test(raw)
    ) {
      message = 'Sign-in was cancelled.';
    }
    try {
      sessionStorage.setItem(EntraAuthService.REDIRECT_ERROR_KEY, message);
      sessionStorage.removeItem('he_social_redirect_pending');
    } catch {
      // ignore
    }
    this.clearStuckInteraction();
    this.clearOAuthParamsFromBrowserUrl();
  }

  /** Remove ?code= / ?state= from the URL after loginRedirect (safe on /redirect and /login). */
  stripOAuthParamsFromBrowserUrl(): void {
    this.clearOAuthParamsFromBrowserUrl();
  }

  private clearOAuthParamsFromBrowserUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const cleanPath = window.location.pathname;
    const search = window.location.search;
    if (!search && !window.location.hash) {
      return;
    }
    const params = new URLSearchParams(search);
    const emailBefore = params.get('email');
    const hadEmailBefore = !!(emailBefore && String(emailBefore).trim());
    const strip = ['code', 'state', 'error', 'error_description', 'client_info'];
    let changed = false;
    for (const key of strip) {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    }
    let hash = window.location.hash;
    if (hash && /error|code|state/i.test(hash)) {
      hash = '';
      changed = true;
    }
    if (!changed) {
      return;
    }
    const nextSearch = params.toString();
    const emailAfter = params.get('email');
    const hadEmailAfter = !!(emailAfter && String(emailAfter).trim());
    const url = cleanPath + (nextSearch ? `?${nextSearch}` : '') + hash;
    if (hadEmailBefore && !hadEmailAfter) {
      console.warn(
        '[auth deep-link] clearOAuthParamsFromBrowserUrl: email query param was removed',
        {
          pathname: cleanPath,
          searchBefore: search,
          searchAfter: nextSearch ? `?${nextSearch}` : '',
          emailBefore,
          strippedOAuthKeys: strip.filter((key) =>
            new URLSearchParams(search).has(key)
          ),
        }
      );
    } else if (hadEmailBefore && changed) {
      console.log(
        '[auth deep-link] clearOAuthParamsFromBrowserUrl: OAuth params stripped; email kept',
        {
          searchBefore: search,
          searchAfter: nextSearch ? `?${nextSearch}` : '',
          email: emailAfter,
        }
      );
    }
    window.history.replaceState({}, document.title, url);
  }

  private async init(): Promise<ICustomAuthPublicClientApplication> {
    this.authClient = await CustomAuthPublicClientApplication.create(
      customAuthConfig
    );
    try {
      if (typeof this.authClient.handleRedirectPromise === 'function') {
        await this.authClient.handleRedirectPromise();
      }
    } catch (err) {
      console.error('MSAL handleRedirectPromise failed', err);
      this.recordRedirectError(err);
    }
    return this.authClient;
  }

  getClient(): Promise<ICustomAuthPublicClientApplication> {
    return this.authClientPromise;
  }

  /** Process loginRedirect response (safe to call again after init). */
  async awaitRedirectResponse(): Promise<void> {
    const client = await this.getClient();
    try {
      if (typeof client.handleRedirectPromise === 'function') {
        await client.handleRedirectPromise();
      }
    } catch (err) {
      console.error('MSAL handleRedirectPromise failed', err);
      this.recordRedirectError(err);
    }
  }

  get currentState(): AuthFlowStateBase | undefined {
    return this.flowState;
  }

  async getCurrentAccount(): Promise<CustomAuthAccountData | null> {
    const client = await this.getClient();
    const result = client.getCurrentAccount();
    if (result.isCompleted()) {
      return result.data ?? null;
    }
    return null;
  }

  /**
   * Renew Entra ID/access tokens using MSAL's cached refresh token.
   * Requires offline_access at sign-in and openid scope for a fresh ID token.
   */
  async refreshAccountTokens(forceRefresh = true): Promise<{
    idToken: string;
    accessToken: string;
    expiresOn: Date | null;
  } | null> {
    const account = await this.getCurrentAccount();
    if (!account) {
      return null;
    }

    const result = await account.getAccessToken({
      forceRefresh,
      scopes: [...ENTRA_TOKEN_SCOPES],
    });

    if (result.isFailed()) {
      const message =
        result.error?.errorData?.errorDescription ||
        'Unable to refresh Entra session. Please sign in again.';
      throw new Error(message);
    }

    if (!result.isCompleted() || !result.data) {
      return null;
    }

    return {
      idToken: result.data.idToken || account.getIdToken?.() || '',
      accessToken: result.data.accessToken || '',
      expiresOn: result.data.expiresOn ?? null,
    };
  }

  /**
   * MSAL keeps the Entra refresh token in browser storage; Custom Auth does not
   * always surface it on AccountData. Read it for SPA handoff + session/refresh.
   */
  async tryExtractRefreshTokenFromCache(
    accountData?: CustomAuthAccountData | null
  ): Promise<string> {
    const account = accountData ?? (await this.getCurrentAccount());
    if (account) {
      try {
        const result = await account.getAccessToken({
          forceRefresh: false,
          scopes: [...ENTRA_TOKEN_SCOPES],
        });
        if (result.isCompleted?.() && result.data) {
          const fromPayload = this.readRefreshFromTokenPayload(result.data);
          if (fromPayload) {
            return fromPayload;
          }
        }
      } catch {
        // fall through to MSAL cache scan
      }
    }
    return this.readRefreshTokenFromMsalStorage();
  }

  /**
   * Best-effort Entra refresh token after native OTP, social sign-in, or silent renew.
   * Tries cache read, then a forced token refresh (offline_access), then MSAL storage scan.
   */
  async resolveEntraRefreshToken(
    accountData?: CustomAuthAccountData | null
  ): Promise<string> {
    const stashed = getStashedEntraRefreshToken();
    if (stashed) {
      return stashed;
    }

    let refresh = await this.tryExtractRefreshTokenFromCache(accountData);
    if (refresh) {
      return refresh;
    }

    const account = accountData ?? (await this.getCurrentAccount());
    if (account) {
      try {
        const forced = await account.getAccessToken({
          forceRefresh: true,
          scopes: [...ENTRA_TOKEN_SCOPES],
        });
        if (forced.isCompleted?.() && forced.data) {
          refresh = this.readRefreshFromTokenPayload(forced.data);
          if (refresh) {
            return refresh;
          }
        }
        refresh = getStashedEntraRefreshToken();
        if (refresh) {
          return refresh;
        }
      } catch (err) {
        console.warn('[entra] Forced token refresh while resolving refresh token failed', err);
      }
      refresh = await this.tryExtractRefreshTokenFromCache(account);
      if (refresh) {
        return refresh;
      }
    }

    return getStashedEntraRefreshToken() || this.readRefreshTokenFromMsalStorage();
  }

  /** ID token from latest Native Auth /token response when account.getIdToken() is stale. */
  getStashedNativeIdToken(): string {
    return getStashedEntraIdToken();
  }

  private readRefreshFromTokenPayload(data: unknown): string {
    if (!data || typeof data !== 'object') {
      return '';
    }
    const record = data as Record<string, unknown>;
    const direct =
      (typeof record['refreshToken'] === 'string' && record['refreshToken']) ||
      (typeof record['refresh_token'] === 'string' && record['refresh_token']);
    if (direct && String(direct).trim()) {
      return String(direct).trim();
    }
    return '';
  }

  private readRefreshTokenFromMsalStorage(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    const clientId = ENTRA_NATIVE_CLIENT_ID.toLowerCase();
    const parseRefreshSecret = (raw: string): string => {
      if (!raw?.trim()) {
        return '';
      }
      try {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const credType = String(
          parsed['credentialType'] ?? parsed['CredentialType'] ?? ''
        ).toLowerCase();
        if (credType === 'refreshtoken') {
          const secret = parsed['secret'] ?? parsed['Secret'];
          if (typeof secret === 'string' && secret.trim().length > 20) {
            return secret.trim();
          }
        }
        for (const field of [
          'secret',
          'refreshToken',
          'refresh_token',
          'credential',
        ]) {
          const value = parsed[field];
          if (
            typeof value === 'string' &&
            value.trim().length > 20 &&
            !value.startsWith('eyJ')
          ) {
            return value.trim();
          }
        }
      } catch {
        if (raw.length > 20 && !raw.startsWith('{') && !raw.startsWith('eyJ')) {
          return raw.trim();
        }
      }
      return '';
    };

    const storages: Storage[] = [];
    try {
      if (typeof localStorage !== 'undefined') {
        storages.push(localStorage);
      }
      if (typeof sessionStorage !== 'undefined') {
        storages.push(sessionStorage);
      }
    } catch {
      return '';
    }

    for (const store of storages) {
      try {
        for (let i = 0; i < store.length; i++) {
          const key = store.key(i);
          if (!key) {
            continue;
          }
          const lowerKey = key.toLowerCase();
          if (
            !lowerKey.includes(clientId) &&
            !lowerKey.startsWith('msal.')
          ) {
            continue;
          }
          const raw = store.getItem(key);
          if (!raw) {
            continue;
          }
          const fromJson = parseRefreshSecret(raw);
          if (fromJson) {
            return fromJson;
          }
          if (/refresh/i.test(key) && raw.length > 20 && !raw.startsWith('{')) {
            return raw.trim();
          }
        }
      } catch {
        // try next storage
      }
    }
    return '';
  }

  async signIn(username: string): Promise<EntraFlowResult> {
    const client = await this.getClient();
    const result = await client.signIn({ username });
    this.flowState = result.state;

    if (result.isFailed()) {
      if (this.isUserNotFoundError(result)) {
        return { step: 'password', userNotFound: true };
      }
      return { step: 'password', error: this.mapSignInError(result) };
    }
    if (result.isPasswordRequired()) {
      return { step: 'password' };
    }
    if (result.isCodeRequired()) {
      return { step: 'code' };
    }
    if (result.isAuthMethodRegistrationRequired()) {
      return {
        step: 'auth-method-registration',
        authMethods: result.state.getAuthMethods(),
      };
    }
    if (result.isMfaRequired()) {
      return {
        step: 'mfa-methods',
        authMethods: result.state.getAuthMethods(),
      };
    }
    if (result.isCompleted()) {
      return { step: 'completed', accountData: result.data };
    }
    return { step: 'password', error: 'Sign-in failed' };
  }

  async submitPassword(password: string): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof SignInPasswordRequiredState)) {
      return { step: 'password', error: 'Password step is not active.' };
    }
    const result = await this.flowState.submitPassword(password);
    this.flowState = result.state;
    return this.mapContinuationResult(result);
  }

  async submitCode(code: string): Promise<EntraFlowResult> {
    if (this.flowState instanceof SignInCodeRequiredState) {
      const result = await this.flowState.submitCode(code);
      this.flowState = result.state;
      return this.mapContinuationResult(result);
    }
    if (this.flowState instanceof SignUpCodeRequiredState) {
      const result = await this.flowState.submitCode(code);
      this.flowState = result.state;
      return this.mapSignUpContinuation(result);
    }
    if (this.flowState instanceof ResetPasswordCodeRequiredState) {
      const result = await this.flowState.submitCode(code);
      this.flowState = result.state;
      if (result.isFailed()) {
        return {
          step: 'code',
          error:
            result.error?.errorData?.errorDescription ||
            'Invalid code or code expired.',
        };
      }
      if (result.isPasswordRequired()) {
        return { step: 'new-password' };
      }
      return { step: 'code', error: 'Unexpected reset password response.' };
    }
    if (this.flowState instanceof MfaVerificationRequiredState) {
      return this.submitMfaChallenge(code);
    }
    if (this.flowState instanceof AuthMethodVerificationRequiredState) {
      return this.submitAuthMethodChallenge(code);
    }
    return { step: 'code', error: 'Code step is not active.' };
  }

  async resendCode(): Promise<EntraFlowResult> {
    if (this.flowState instanceof SignInCodeRequiredState) {
      const result = await this.flowState.resendCode();
      this.flowState = result.state;
      if (result.isFailed()) {
        return {
          step: 'code',
          error:
            result.error?.errorData?.errorDescription ||
            'An error occurred while resending the code',
        };
      }
      return { step: 'code' };
    }
    if (this.flowState instanceof SignUpCodeRequiredState) {
      const result = await this.flowState.resendCode();
      this.flowState = result.state;
      if (result.isFailed()) {
        return {
          step: 'code',
          error:
            result.error?.errorData?.errorDescription ||
            'An error occurred while resending the code',
        };
      }
      return { step: 'code' };
    }
    if (this.flowState instanceof ResetPasswordCodeRequiredState) {
      const result = await this.flowState.resendCode();
      this.flowState = result.state;
      if (result.isFailed()) {
        return {
          step: 'code',
          error:
            result.error?.errorData?.errorDescription ||
            'An error occurred while resending the code',
        };
      }
      return { step: 'code' };
    }
    return { step: 'code', error: 'Resend is not available.' };
  }

  async requestMfaChallenge(methodId: string): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof MfaAwaitingState)) {
      return { step: 'mfa-methods', error: 'MFA step is not active.' };
    }
    const result = await this.flowState.requestChallenge(methodId);
    this.flowState = result.state;
    if (result.isFailed()) {
      return {
        step: 'mfa-methods',
        error:
          result.error?.errorData?.errorDescription ||
          'An error occurred while requesting MFA challenge',
      };
    }
    if (result.isVerificationRequired()) {
      return { step: 'mfa-challenge' };
    }
    return { step: 'mfa-methods', error: 'Unexpected MFA response.' };
  }

  async submitMfaChallenge(code: string): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof MfaVerificationRequiredState)) {
      return { step: 'mfa-challenge', error: 'MFA challenge step is not active.' };
    }
    const result = await this.flowState.submitChallenge(code);
    this.flowState = result.state;
    if (result.isFailed()) {
      return {
        step: 'mfa-challenge',
        error:
          result.error?.isIncorrectChallenge()
            ? 'Incorrect code.'
            : result.error?.errorData?.errorDescription ||
              'An error occurred while verifying the challenge response',
      };
    }
    if (result.isCompleted()) {
      return { step: 'completed', accountData: result.data };
    }
    return { step: 'mfa-challenge', error: 'Unexpected MFA response.' };
  }

  async challengeAuthMethod(
    authMethodType: AuthenticationMethod,
    verificationContact: string
  ): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof AuthMethodRegistrationRequiredState)) {
      return {
        step: 'auth-method-registration',
        error: 'Auth method registration step is not active.',
      };
    }
    const result = await this.flowState.challengeAuthMethod({
      authMethodType,
      verificationContact,
    });
    this.flowState = result.state;
    if (result.isFailed()) {
      return {
        step: 'auth-method-registration',
        error:
          result.error?.errorData?.errorDescription ||
          'An error occurred while verifying the authentication method',
      };
    }
    if (result.isCompleted()) {
      return { step: 'completed', accountData: result.data };
    }
    if (result.isVerificationRequired()) {
      return { step: 'auth-method-challenge' };
    }
    return {
      step: 'auth-method-registration',
      error: 'Unexpected auth method registration response.',
    };
  }

  async submitAuthMethodChallenge(code: string): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof AuthMethodVerificationRequiredState)) {
      return {
        step: 'auth-method-challenge',
        error: 'Auth method challenge step is not active.',
      };
    }
    const result = await this.flowState.submitChallenge(code);
    this.flowState = result.state;
    if (result.isFailed()) {
      return {
        step: 'auth-method-challenge',
        error:
          result.error?.isIncorrectChallenge()
            ? 'Incorrect code.'
            : result.error?.errorData?.errorDescription ||
              'An error occurred while verifying the challenge response',
      };
    }
    if (result.isCompleted()) {
      return { step: 'completed', accountData: result.data };
    }
    return {
      step: 'auth-method-challenge',
      error: 'Unexpected auth method challenge response.',
    };
  }

  async signUp(
    email: string,
    attributes: UserAccountAttributes
  ): Promise<EntraFlowResult> {
    const client = await this.getClient();
    const result = await client.signUp({ username: email, attributes });
    this.flowState = result.state;

    if (result.isFailed()) {
      if (this.isUserAlreadyExistsError(result)) {
        return { step: 'password', userAlreadyExists: true };
      }
      return {
        step: 'password',
        error:
          result.error?.errorData?.errorDescription || 'Sign-up failed',
      };
    }
    if (result.isPasswordRequired()) {
      return { step: 'password' };
    }
    if (result.isCodeRequired()) {
      return { step: 'code' };
    }
    if (result.isCompleted()) {
      return this.autoSignInAfterSignUp();
    }
    return { step: 'password', error: 'Sign-up failed' };
  }

  async submitSignUpPassword(password: string): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof SignUpPasswordRequiredState)) {
      return { step: 'password', error: 'Sign-up password step is not active.' };
    }
    const result = await this.flowState.submitPassword(password);
    this.flowState = result.state;
    return this.mapSignUpContinuation(result);
  }

  async resetPassword(username: string): Promise<EntraFlowResult> {
    const client = await this.getClient();
    const result = await client.resetPassword({ username });
    this.flowState = result.state;

    if (result.isFailed()) {
      return {
        step: 'code',
        error:
          result.error?.errorData?.errorDescription ||
          'Password reset failed',
      };
    }
    if (result.isCodeRequired()) {
      return { step: 'code' };
    }
    return { step: 'code', error: 'Password reset failed' };
  }

  async submitNewPassword(password: string): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof ResetPasswordPasswordRequiredState)) {
      return {
        step: 'new-password',
        error: 'New password step is not active.',
      };
    }
    const result = await this.flowState.submitNewPassword(password);
    this.flowState = result.state;
    if (result.isFailed()) {
      return {
        step: 'new-password',
        error:
          result.error?.errorData?.errorDescription ||
          'Failed to set new password',
      };
    }
    if (result.isCompleted()) {
      return this.autoSignInAfterReset();
    }
    return { step: 'new-password', error: 'Unexpected reset password response.' };
  }

  async signOut(options?: { localOnly?: boolean }): Promise<void> {
    if (!options?.localOnly) {
      const account = await this.getCurrentAccount();
      if (account?.signOut) {
        try {
          await account.signOut();
        } catch (err) {
          console.error('Entra account signOut failed', err);
        }
      }
    }
    this.flowState = undefined;
    this.clearStuckInteraction();
    this.clearMsalBrowserCache();
  }

  /** Remove MSAL custom-auth cache so getCurrentAccount() cannot re-hydrate a session. */
  clearMsalBrowserCache(): void {
    try {
      const clientId = ENTRA_NATIVE_CLIENT_ID.toLowerCase();
      const tenantHints = [
        'login.healthexam.ai',
        'ciamlogin.com',
        '09b5f110-358e-4126-a1ea-5a711a813cc2',
      ];
      const removeKeys = (storage: Storage) => {
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i);
          if (!key) {
            continue;
          }
          const lower = key.toLowerCase();
          if (
            key.startsWith('msal.') ||
            lower.includes(clientId) ||
            tenantHints.some((hint) => lower.includes(hint))
          ) {
            keys.push(key);
          }
        }
        keys.forEach((k) => storage.removeItem(k));
      };
      removeKeys(localStorage);
      removeKeys(sessionStorage);
    } catch {
      // ignore storage errors
    }
  }

  /**
   * Federated Google / Apple sign-in via MSAL loginPopup.
   * Separate from native email OTP (client.signIn / signUp).
   *
   * - Apple: domain_hint=apple (skips Entra email page on desktop)
   * - Google: omit domain_hint on desktop (AADSTS90023 with domain_hint=google)
   */
  async signInWithSocial(
    provider: SocialProvider
  ): Promise<EntraFlowResult> {
    this.clearStuckInteraction();
    const client = await this.getClient();

    try {
      await client.loginPopup(this.buildSocialRequest(provider));
    } catch (err: unknown) {
      this.clearStuckInteraction();
      const message =
        err instanceof Error
          ? err.message
          : `An unexpected error occurred while signing in with ${provider}`;
      if (/user_cancelled|popup_window_error|closed/i.test(message)) {
        return { step: 'password', error: 'Sign-in was cancelled.' };
      }
      if (/interaction_in_progress/i.test(message)) {
        this.clearStuckInteraction();
        return {
          step: 'password',
          error:
            'Another sign-in is already in progress. Close any popups, refresh, and try again.',
        };
      }
      return { step: 'password', error: message };
    }

    return this.readSocialAccountResult(provider);
  }

  /**
   * Same Google/Apple federation, but full-page loginRedirect.
   * Use for Lovable deep-links — works automatically without a user click
   * (popups are blocked after cross-site redirect; redirects are not).
   * Navigates away; does not return a completed result in this call.
   */
  async signInWithSocialRedirect(provider: SocialProvider): Promise<void> {
    this.clearStuckInteraction();
    const client = await this.getClient();
    const request = this.buildSocialRedirectRequest(provider);

    logGoogleFlow('entra:signInWithSocialRedirect', {
      provider,
      redirectUri: customAuthConfig.auth.redirectUri,
      request,
    });

    try {
      if (typeof client.loginRedirect === 'function') {
        logGoogleFlow('entra:loginRedirect → Google/Entra (leaving dev-auth)');
        await client.loginRedirect(request);
        return;
      }
    } catch (err) {
      console.warn('MSAL loginRedirect failed; using authorize URL', err);
      logGoogleFlow('entra:loginRedirect failed, fallback authorize URL', {
        err,
      });
    }

    const fallback = this.buildEntraSocialAuthorizeUrl(provider);
    logGoogleFlowRedirect('entra:location.assign fallback authorize', fallback);
    window.location.assign(fallback);
  }

  /** Popup flow — keep Google without domain_hint on desktop (AADSTS90023). */
  private buildSocialRequest(provider: SocialProvider): Record<string, unknown> {
    const request: Record<string, unknown> = {
      authority: customAuthConfig.auth.authority,
      scopes: [...ENTRA_TOKEN_SCOPES],
      redirectUri: customAuthConfig.auth.redirectUri || '',
    };
    if (provider === 'apple') {
      request['domainHint'] = 'Apple';
      request['extraQueryParameters'] = { domain_hint: 'Apple' };
    }
    return request;
  }

  /**
   * Full-page redirect — domainHint must match Entra IdP name exactly:
   * Google | Apple (see Microsoft native-auth social sign-in tutorial).
   * Omit prompt=login so Entra can SSO; forced login increases hosted “Create account” steps.
   */
  private buildSocialRedirectRequest(
    provider: SocialProvider
  ): Record<string, unknown> {
    const domainHint = provider === 'google' ? 'Google' : 'Apple';
    return {
      authority: customAuthConfig.auth.authority,
      scopes: [...ENTRA_TOKEN_SCOPES],
      redirectUri: customAuthConfig.auth.redirectUri || '',
      domainHint,
      extraQueryParameters: { domain_hint: domainHint },
    };
  }

  private buildEntraSocialAuthorizeUrl(provider: SocialProvider): string {
    const redirectUri = customAuthConfig.auth.redirectUri || '';
    const domainHint = provider === 'google' ? 'Google' : 'Apple';
    const authority = (customAuthConfig.auth.authority || '').replace(/\/?$/, '/');
    const params = new URLSearchParams({
      client_id: ENTRA_NATIVE_CLIENT_ID,
      response_type: 'code',
      redirect_uri: redirectUri,
      scope: ENTRA_TOKEN_SCOPES.join(' '),
      domain_hint: domainHint,
    });
    return `${authority}oauth2/v2.0/authorize?${params.toString()}`;
  }

  private readSocialAccountResult(provider: SocialProvider): EntraFlowResult {
    // getClient is sync after init; callers already awaited getClient via loginPopup path
    const client = this.authClient;
    if (!client) {
      return {
        step: 'password',
        error: `Could not complete ${provider} sign-in.`,
      };
    }
    const accountResult = client.getCurrentAccount();
    if (accountResult.isFailed?.()) {
      return {
        step: 'password',
        error:
          accountResult.error?.errorData?.errorDescription ||
          'Signed in with the identity provider, but no account was found in cache.',
      };
    }
    if (accountResult.isCompleted?.() && accountResult.data) {
      return { step: 'completed', accountData: accountResult.data };
    }
    return {
      step: 'password',
      error: `Could not complete ${provider} sign-in.`,
    };
  }

  /** Clear stuck MSAL popup locks (interaction_in_progress). */
  clearStuckInteraction(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (/interactionstatus|interaction.status|interaction_in_progress/i.test(
            key
          ) ||
            key.includes('interaction.status'))
        ) {
          keys.push(key);
        }
      }
      keys.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // ignore
    }
  }

  private async autoSignInAfterSignUp(): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof SignUpCompletedState)) {
      return { step: 'completed', error: 'Sign-up completed state missing.' };
    }
    const result = await this.flowState.signIn();
    this.flowState = result.state;
    return this.mapContinuationResult(result);
  }

  private async autoSignInAfterReset(): Promise<EntraFlowResult> {
    if (!(this.flowState instanceof ResetPasswordCompletedState)) {
      return { step: 'completed', error: 'Reset completed state missing.' };
    }
    const result = await this.flowState.signIn();
    this.flowState = result.state;
    return this.mapContinuationResult(result);
  }

  private mapContinuationResult(result: {
    isFailed: () => boolean;
    isCompleted: () => boolean;
    isAuthMethodRegistrationRequired?: () => boolean;
    isMfaRequired?: () => boolean;
    isVerificationRequired?: () => boolean;
    error?: any;
    data?: CustomAuthAccountData;
    state: AuthFlowStateBase;
  }): EntraFlowResult {
    if (result.isCompleted()) {
      return { step: 'completed', accountData: result.data };
    }
    if (result.isAuthMethodRegistrationRequired?.()) {
      const state = result.state as AuthMethodRegistrationRequiredState;
      return {
        step: 'auth-method-registration',
        authMethods: state.getAuthMethods(),
      };
    }
    if (this.flowState instanceof AuthMethodRegistrationRequiredState) {
      return {
        step: 'auth-method-registration',
        authMethods: this.flowState.getAuthMethods(),
      };
    }
    if (result.isMfaRequired?.()) {
      const state = result.state as MfaAwaitingState;
      return {
        step: 'mfa-methods',
        authMethods: state.getAuthMethods(),
      };
    }
    if (this.flowState instanceof MfaAwaitingState) {
      return {
        step: 'mfa-methods',
        authMethods: this.flowState.getAuthMethods(),
      };
    }
    if (result.isVerificationRequired?.()) {
      return { step: 'auth-method-challenge' };
    }
    if (this.flowState instanceof AuthMethodVerificationRequiredState) {
      return { step: 'auth-method-challenge' };
    }
    if (this.flowState instanceof MfaVerificationRequiredState) {
      return { step: 'mfa-challenge' };
    }
    if (result.isFailed()) {
      const description =
        result.error?.errorData?.errorDescription ||
        result.error?.errorData?.error_description ||
        '';
      if (/registration_required|50072|multi-factor authentication/i.test(description)) {
        if (this.flowState instanceof AuthMethodRegistrationRequiredState) {
          return {
            step: 'auth-method-registration',
            authMethods: this.flowState.getAuthMethods(),
          };
        }
      }
      return {
        step: 'password',
        error:
          result.error?.isInvalidPassword?.()
            ? 'Incorrect password'
            : result.error?.isInvalidCode?.()
              ? 'Invalid code'
              : description || 'An error occurred',
      };
    }
    return { step: 'password', error: 'Unexpected authentication response.' };
  }

  getRegistrationAuthMethods(): AuthenticationMethod[] {
    if (this.flowState instanceof AuthMethodRegistrationRequiredState) {
      return this.flowState.getAuthMethods();
    }
    return [];
  }

  pickSmsAuthMethod(
    methods: AuthenticationMethod[]
  ): AuthenticationMethod | undefined {
    if (!methods.length) {
      return undefined;
    }
    return (
      methods.find((method) =>
        ['sms', 'phone'].includes(
          (method.challenge_channel || '').toString().toLowerCase()
        )
      ) || methods[0]
    );
  }

  private async mapSignUpContinuation(result: {
    isFailed: () => boolean;
    isCompleted: () => boolean;
    isPasswordRequired?: () => boolean;
    isCodeRequired?: () => boolean;
    error?: any;
    state: AuthFlowStateBase;
  }): Promise<EntraFlowResult> {
    if (result.isFailed()) {
      return {
        step: 'password',
        error:
          result.error?.errorData?.errorDescription ||
          'An error occurred during sign-up',
      };
    }
    if (result.isPasswordRequired?.()) {
      return { step: 'password' };
    }
    if (result.isCodeRequired?.()) {
      return { step: 'code' };
    }
    if (result.isCompleted()) {
      return this.autoSignInAfterSignUp();
    }
    return { step: 'password', error: 'Unexpected sign-up response.' };
  }

  private isUserNotFoundError(result: {
    error?: {
      isUserNotFound?: () => boolean;
      errorData?: { error?: string; errorDescription?: string };
    };
  }): boolean {
    if (result.error?.isUserNotFound?.()) {
      return true;
    }
    const data = result.error?.errorData;
    const blob = `${data?.error || ''} ${data?.errorDescription || ''}`;
    return /aadsts50034|user not found|does not exist in the.*directory|user account.*does not exist/i.test(
      blob
    );
  }

  private isUserAlreadyExistsError(result: {
    error?: {
      isUserAlreadyExists?: () => boolean;
      errorData?: { error?: string; errorDescription?: string };
    };
  }): boolean {
    if (result.error?.isUserAlreadyExists?.()) {
      return true;
    }
    const data = result.error?.errorData;
    const blob = `${data?.error || ''} ${data?.errorDescription || ''}`;
    return /user_already_exists|aadsts1003037|\b1003037\b/i.test(blob);
  }

  private mapSignInError(result: {
    error?: {
      isUserNotFound?: () => boolean;
      isInvalidUsername?: () => boolean;
      isPasswordIncorrect?: () => boolean;
      isRedirectRequired?: () => boolean;
      errorData?: { errorDescription?: string };
    };
  }): string {
    if (result.error?.isUserNotFound?.()) {
      return 'User not found';
    }
    if (result.error?.isInvalidUsername?.()) {
      return 'Username is invalid';
    }
    if (result.error?.isPasswordIncorrect?.()) {
      return 'Password is invalid';
    }
    if (result.error?.isRedirectRequired?.()) {
      return 'Native authentication is not available for this account. Sign up with a local customer email first (federated/guest accounts are not supported).';
    }
    return result.error?.errorData?.errorDescription || 'Sign-in failed';
  }
}
